import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
  Autocomplete,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { useLeadDetails } from '../../contexts/LeadDetailsContext';
import { useLead } from '../../contexts/LeadContext';

const T = {
  green: '#0a6b3e',
  greenLt: '#e5f5ec',
  greenMd: '#0d8a50',
  greenSf: 'rgba(10,107,62,0.06)',
  ink: '#0f1a13',
  subtle: '#526b5c',
  muted: '#8eaa98',
  bg: '#f5f8f6',
  border: '#dce8e1',
  borderL: '#eaf1ec',
  white: '#ffffff',
  red: '#dc2626',
  redLt: '#fef2f2',
  redBorder: '#fecaca',
  font: "'Inter','DM Sans','Plus Jakarta Sans',sans-serif",
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontFamily: T.font,
    fontSize: 13,
    borderRadius: 2,
    bgcolor: T.white,
    '& fieldset': { borderColor: T.borderL },
    '&:hover fieldset': { borderColor: T.border },
    '&.Mui-focused fieldset': { borderColor: T.green },
  },
  '& .MuiInputLabel-root': {
    fontFamily: T.font,
    fontSize: 13,
    color: T.subtle,
    '&.Mui-focused': { color: T.green },
  },
  '& .MuiOutlinedInput-input::placeholder': {
    fontFamily: T.font,
    opacity: 0.65,
  },
  '& .MuiChip-root': {
    fontFamily: T.font,
    fontSize: 12,
    height: 26,
    bgcolor: T.greenLt,
    color: T.green,
    border: `1px solid ${T.border}`,
    '& .MuiChip-deleteIcon': { color: T.subtle, '&:hover': { color: T.green } },
  },
};

const normalizeTag = (tag) => {
  if (tag == null || tag === '') return '';
  if (typeof tag === 'object') return String(tag.value ?? tag.label ?? '').trim();
  return String(tag).trim();
};

const TagSelector = ({ leadTags = [], value = [], onChange, placeholder }) => {
  const optionStrings = (leadTags || []).map(normalizeTag).filter(Boolean);
  const stringValue = (value || []).map(normalizeTag).filter(Boolean);

  const handleChange = (event, newValue) => {
    const uniqueTags = newValue
      .map((t) => normalizeTag(t))
      .filter(Boolean)
      .filter((tag, index, self) => self.findIndex((x) => x.toLowerCase() === tag.toLowerCase()) === index);
    onChange(uniqueTags);
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      options={optionStrings}
      value={stringValue}
      onChange={handleChange}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : normalizeTag(opt))}
      filterSelectedOptions
      componentsProps={{
        paper: {
          sx: {
            fontFamily: T.font,
            borderRadius: 2,
            border: `1px solid ${T.borderL}`,
            boxShadow: '0 8px 24px rgba(15, 26, 19, 0.08)',
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label="Tags"
          placeholder={placeholder || 'Type and press Enter, or pick from the list'}
          sx={fieldSx}
        />
      )}
    />
  );
};

const TagsUpdate = ({ open, onClose, leadId, leadIds: leadIdsProp, leadTags }) => {
  const {
    preferences,
    fetchLeadPreference,
    setPreferences,
    refreshActivities,
  } = useLeadDetails();
  const [loading, setLoading] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [saveError, setSaveError] = useState(null);

  const { saveTags } = useLead();

  const resolvedLeadIds = useMemo(() => {
    if (leadIdsProp && leadIdsProp.length > 0) return leadIdsProp;
    if (leadId != null && leadId !== '') return [leadId];
    return [];
  }, [leadId, leadIdsProp]);

  const isBulk = resolvedLeadIds.length > 1;
  const leadCount = resolvedLeadIds.length;

  const title = isBulk ? 'Tag selected leads' : 'Manage tags';
  const subtitle = isBulk
    ? `Add or remove tags to categorize these ${leadCount} leads. The same tags are applied to each selected lead.`
    : 'Add or remove tags to categorize this lead.';

  const tagPlaceholder = isBulk
    ? 'Tags apply to all selected leads — type and press Enter, or pick from the list'
    : 'Type and press Enter, or pick from the list';

  const saveLabel = isBulk ? `Apply tags to ${leadCount} leads` : 'Save changes';

  useEffect(() => {
    if (!open) return;
    setSaveError(null);
    if (isBulk) {
      setSelectedTags([]);
    } else if (preferences?.saved_preferences) {
      setSelectedTags(preferences.saved_preferences.tags || []);
    } else {
      setSelectedTags([]);
    }
  }, [open, isBulk, preferences]);

  const handleSave = async () => {
    if (resolvedLeadIds.length === 0) {
      setSaveError('No lead selected.');
      return;
    }
    setLoading(true);
    setSaveError(null);
    try {
      const tagValues = selectedTags.map((tag) => {
        if (typeof tag === 'object' && tag?.value) return tag.value;
        return tag;
      });

      const uniqueTagValues = tagValues
        .map((t) => (t == null ? '' : String(t).trim()))
        .filter(Boolean)
        .filter((tag, index, self) => self.findIndex((x) => x.toLowerCase() === tag.toLowerCase()) === index);

      await saveTags({ lead_ids: resolvedLeadIds, tags: uniqueTagValues });

      if (!isBulk && preferences) {
        const updatedPreferences = {
          ...preferences,
          saved_preferences: {
            ...preferences.saved_preferences,
            tags: uniqueTagValues,
          },
        };
        setPreferences(updatedPreferences);
      } else if (!isBulk) {
        await fetchLeadPreference(resolvedLeadIds[0]);
      }

      if (!isBulk && resolvedLeadIds[0]) {
        await refreshActivities(resolvedLeadIds[0]);
      }
      onClose();
    } catch (err) {
      setSaveError(err?.message || 'Could not save tags. Try again.');
      if (resolvedLeadIds[0] != null) {
        await fetchLeadPreference(resolvedLeadIds[0]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 480,
          width: '100%',
          borderRadius: 3,
          fontFamily: T.font,
          overflow: 'hidden',
          m: 2,
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: T.greenLt,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <LocalOfferOutlinedIcon sx={{ fontSize: 22, color: T.green }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: T.font, fontSize: 16, fontWeight: 700, color: T.ink, lineHeight: 1.3 }}>
                {title}
              </Typography>
              {isBulk && (
                <Typography sx={{ fontFamily: T.font, fontSize: 11, fontWeight: 600, color: T.green, mt: 0.35 }}>
                  {leadCount} leads selected
                </Typography>
              )}
              <Typography sx={{ fontFamily: T.font, fontSize: 11.5, color: T.muted, mt: isBulk ? 0.5 : 0.25, lineHeight: 1.45 }}>
                {subtitle}
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            disabled={loading}
            sx={{ color: T.muted, '&:hover': { color: T.ink } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider sx={{ borderColor: T.borderL }} />

      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
        <TagSelector
          leadTags={leadTags || []}
          value={selectedTags}
          onChange={setSelectedTags}
          placeholder={tagPlaceholder}
        />

        {saveError && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${T.redBorder}`,
              bgcolor: T.redLt,
            }}
          >
            <Typography sx={{ fontFamily: T.font, fontSize: 12.5, color: T.red }}>{saveError}</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          gap: 1,
          justifyContent: 'flex-end',
          borderTop: `1px solid ${T.borderL}`,
          bgcolor: T.bg,
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 500,
            color: T.subtle,
            textTransform: 'none',
            borderRadius: 2,
            px: 2,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || resolvedLeadIds.length === 0}
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
            px: 2.5,
            minWidth: isBulk ? 220 : 132,
            bgcolor: T.green,
            color: '#fff',
            boxShadow: 'none',
            '&:hover': { bgcolor: T.greenMd, boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: T.borderL, color: T.muted },
          }}
        >
          {loading ? <CircularProgress size={22} thickness={4} sx={{ color: '#fff' }} /> : saveLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TagsUpdate;
